import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  UtensilsCrossed,
  ChevronRight,
  Shuffle,
  Plus,
  Minus,
  X,
  Check,
  AlertTriangle,
  ArrowUpRight,
  Apple,
  ChefHat,
  Hammer,
  Info,
} from "lucide-react";
import {
  scoreMeal,
  matchLabel,
  suggestMealForSlot,
  sumTotals,
  remaining,
  TOPUP_FOODS,
  portionsFromTopups,
} from "@/lib/mealPlanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/meals`;
const SLOTS = ["breakfast", "lunch", "dinner"];

/* ---------- Independence disclaimer ---------- */

function IndependenceNoticeLong({ provider }) {
  const text = (provider.disclaimer_long || "").replace(/\{provider_name\}/g, provider.name);
  return (
    <div
      className="border border-black/15 bg-[#F4F3EF] p-5 md:p-6"
      data-testid="meal-independence-long"
    >
      <div className="flex items-center gap-3">
        <Info className="w-4 h-4 text-[#031D47]" strokeWidth={1.75} />
        <p className="dta-overline text-navy">Independent meal planning notice</p>
      </div>
      <p className="mt-3 text-sm text-[#272727] leading-relaxed">{text}</p>
    </div>
  );
}

function IndependenceNoticeShort({ provider }) {
  const text = (provider.disclaimer_short || "").replace(/\{provider_name\}/g, provider.name);
  return (
    <p className="text-[0.7rem] text-[#272727]/60 leading-relaxed mt-3" data-testid="meal-independence-short">
      {text}
    </p>
  );
}

/* ---------- Meal card ---------- */

function MealCard({ meal, slotRemaining, onSwap, onRemove }) {
  const score = scoreMeal(meal, slotRemaining || meal);
  const label = matchLabel(score);
  return (
    <div className="border border-black/10 bg-white p-5" data-testid={`meal-card-${meal.meal_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="dta-overline text-[0.6rem] text-navy">{meal.meal_type}</p>
          <h4 className="font-display uppercase font-bold text-lg md:text-xl mt-1 leading-tight">
            {meal.name}
          </h4>
          {meal.dietary_tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {meal.dietary_tags.map((t) => (
                <span
                  key={t}
                  className="text-[0.65rem] uppercase tracking-wider border border-black/15 px-1.5 py-0.5 text-[#272727]"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="font-display font-black text-2xl md:text-3xl text-[#031D47] leading-none">
            {score}%
          </p>
          <p className="dta-overline text-[0.55rem] text-[#272727]/70 mt-1">{label}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-2 text-center border-y border-black/10 py-3">
        <Stat v={meal.calories} u="kcal" />
        <Stat v={`${meal.protein}g`} u="protein" />
        <Stat v={`${meal.carbs}g`} u="carbs" />
        <Stat v={`${meal.fat}g`} u="fat" />
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onSwap}
          data-testid={`meal-swap-${meal.meal_type}`}
          className="dta-overline text-[0.65rem] inline-flex items-center gap-2 text-[#031D47] hover:opacity-70"
        >
          <Shuffle className="w-3.5 h-3.5" strokeWidth={1.75} /> Swap meal
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="dta-overline text-[0.65rem] inline-flex items-center gap-1 text-[#272727]/70 hover:text-[#031D47]"
            data-testid={`meal-remove-${meal.meal_type}`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.75} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ v, u }) {
  return (
    <div>
      <p className="font-display font-bold text-sm md:text-base leading-none">{v}</p>
      <p className="dta-overline text-[0.55rem] text-[#272727]/60 mt-1">{u}</p>
    </div>
  );
}

/* ---------- Summary table ---------- */

function SummaryTable({ target, planned }) {
  const rows = [
    { k: "Calories", t: target.calories, p: planned.calories, u: "" },
    { k: "Protein", t: target.protein, p: planned.protein, u: "g" },
    { k: "Carbohydrates", t: target.carbs, p: planned.carbs, u: "g" },
    { k: "Fat", t: target.fat, p: planned.fat, u: "g" },
  ];
  const closeMsg = (() => {
    const kcalDelta = Math.abs(target.calories - planned.calories);
    const pDelta = Math.abs(target.protein - planned.protein);
    if (kcalDelta <= 100 && pDelta <= 10) return "You're close to today's target.";
    if (kcalDelta <= 250) return "You're broadly on track for today.";
    return "Try the top-ups below to move closer to your target.";
  })();

  return (
    <div className="bg-white border border-black/10 p-5" data-testid="meal-summary">
      <p className="dta-overline text-navy">Daily summary</p>
      <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-2">
        Planned so far.
      </h4>
      <table className="w-full mt-4 text-sm">
        <thead>
          <tr className="dta-overline text-[0.6rem] text-[#272727]/70">
            <th className="text-left pb-2">Nutrient</th>
            <th className="text-right pb-2">Target</th>
            <th className="text-right pb-2">Planned</th>
            <th className="text-right pb-2">Remaining</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const remaining = Math.round(r.t - r.p);
            return (
              <tr key={r.k} className="border-t border-black/10">
                <td className="py-2 text-[#272727]">{r.k}</td>
                <td className="py-2 text-right font-medium">
                  {Math.round(r.t).toLocaleString()}
                  {r.u}
                </td>
                <td className="py-2 text-right font-medium">
                  {Math.round(r.p).toLocaleString()}
                  {r.u}
                </td>
                <td
                  className={`py-2 text-right font-display font-bold ${
                    remaining <= 0 ? "text-[#4E5B43]" : "text-[#031D47]"
                  }`}
                >
                  {remaining <= 0 ? `+${Math.abs(remaining)}` : remaining}
                  {r.u}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-4 text-sm text-[#272727]/85 italic">{closeMsg}</p>
    </div>
  );
}

/* ---------- Five-a-day tracker ---------- */

function FiveADay({ portions, onInc, onDec }) {
  const dots = Array.from({ length: 5 }, (_, i) => i < portions);
  return (
    <div className="bg-white border border-black/10 p-5" data-testid="five-a-day">
      <div className="flex items-center gap-3">
        <Apple className="w-4 h-4 text-[#4E5B43]" strokeWidth={1.75} />
        <p className="dta-overline text-navy">Fruit &amp; Veg</p>
      </div>
      <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-2">
        5 portions a day.
      </h4>
      <div className="mt-5 flex items-center gap-2">
        {dots.map((on, i) => (
          <span
            key={i}
            className={`w-4 h-4 rounded-full ${on ? "bg-[#4E5B43]" : "bg-black/15"}`}
            data-testid={`five-a-day-dot-${i}`}
          />
        ))}
        <span className="ml-3 dta-overline text-[0.7rem] text-[#272727]">
          {portions} / 5 portions
        </span>
      </div>
      <div className="mt-5 flex gap-2">
        <button
          type="button"
          onClick={onDec}
          className="dta-btn dta-btn-outline-dark px-4 py-2 text-xs"
          data-testid="five-a-day-dec"
        >
          <Minus className="w-3 h-3" strokeWidth={2} />
          Minus one
        </button>
        <button
          type="button"
          onClick={onInc}
          className="dta-btn dta-btn-dark px-4 py-2 text-xs"
          data-testid="five-a-day-inc"
        >
          <Plus className="w-3 h-3" strokeWidth={2} />
          Add portion
        </button>
      </div>
      <p className="mt-4 text-xs text-[#272727]/60 leading-relaxed">
        Work towards at least 5 portions of fruit and vegetables each day. Prepared meal vegetables are not counted automatically.
      </p>
    </div>
  );
}

/* ---------- Top-up picker ---------- */

function TopupPicker({ onAdd }) {
  return (
    <div className="bg-white border border-black/10 p-5" data-testid="topup-picker">
      <p className="dta-overline text-navy">Complete your day</p>
      <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-2">
        Simple additions.
      </h4>
      <p className="text-xs text-[#272727]/70 mt-2">
        Tap to add. Everyday foods to help move closer to your target.
      </p>
      <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {TOPUP_FOODS.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onAdd(t)}
              data-testid={`topup-${t.id}`}
              className="w-full flex items-center justify-between gap-3 text-left border border-black/15 px-3 py-2.5 hover:border-[#031D47] hover:bg-[#031D47]/5 transition-colors"
            >
              <span className="text-sm text-[#272727]">{t.name}</span>
              <span className="dta-overline text-[0.55rem] text-[#272727]/70">
                {t.calories} · {t.protein}p
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Allergen gate + outbound link ---------- */

function OutboundGate({ provider, onProceed, onCancel }) {
  const [ack, setAck] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="allergen-modal"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-[#F4F3EF] border border-black/15 max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-[0_40px_90px_rgba(0,0,0,0.5)]">
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-[#031D47]" strokeWidth={1.75} />
            <p className="dta-overline text-navy">Important allergen notice</p>
          </div>
          <h3 className="font-display uppercase font-bold text-2xl md:text-3xl mt-3 leading-tight">
            Check ingredients<br />with {provider.name}.
          </h3>
          <div className="mt-5 space-y-3 text-sm md:text-base text-[#272727] leading-relaxed">
            <p>
              DTA meal suggestions are provided for general nutritional planning purposes only. Ingredients, recipes and allergen information can change.
            </p>
            <p>
              Always check the latest ingredient and allergen information directly with the meal provider before ordering or consuming any meal.
            </p>
            <p>
              If you have a food allergy or intolerance, do not rely solely on nutritional or meal information displayed by DTA.
            </p>
          </div>
          <label className="mt-6 border-t border-black/15 pt-5 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#031D47] shrink-0"
              data-testid="allergen-ack"
            />
            <span className="text-sm md:text-base text-[#272727]">
              I understand that I must check current ingredient and allergen information with the meal provider before ordering or consuming any meal.
            </span>
          </label>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="dta-btn dta-btn-outline-dark"
              data-testid="allergen-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onProceed}
              disabled={!ack}
              className={`dta-btn dta-btn-dark ${!ack ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="allergen-continue"
            >
              Visit {provider.name}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Day builder ---------- */

function DayBuilder({ provider, catalogue, dailyTarget, selectedByType, setSelectedByType }) {
  const topups = selectedByType.topups || [];
  const meals = SLOTS.map((s) => selectedByType[s]).filter(Boolean);
  const planned = sumTotals([...meals, ...topups]);
  const [portions, setPortions] = useState(0);

  const swap = (slot) => {
    const current = selectedByType[slot];
    const exclude = current ? [current.id] : [];
    const next = suggestMealForSlot({
      slot,
      dailyTarget,
      planned,
      catalogue,
      excludeIds: exclude,
    });
    if (next) setSelectedByType({ ...selectedByType, [slot]: next });
  };

  const remove = (slot) => {
    const copy = { ...selectedByType };
    copy[slot] = null;
    setSelectedByType(copy);
  };

  const addMealForSlot = (slot) => {
    const next = suggestMealForSlot({ slot, dailyTarget, planned, catalogue });
    if (next) setSelectedByType({ ...selectedByType, [slot]: next });
  };

  const addTopup = (food) => {
    setSelectedByType({ ...selectedByType, topups: [...topups, { ...food, _uid: Date.now() + Math.random() }] });
  };

  const removeTopup = (uid) => {
    setSelectedByType({ ...selectedByType, topups: topups.filter((t) => t._uid !== uid) });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* Meal slots */}
      <div className="lg:col-span-7 space-y-4">
        {SLOTS.map((slot) => {
          const meal = selectedByType[slot];
          if (!meal) {
            return (
              <button
                key={slot}
                type="button"
                onClick={() => addMealForSlot(slot)}
                data-testid={`add-meal-${slot}`}
                className="w-full text-left border border-dashed border-black/25 p-5 flex items-center justify-between gap-4 hover:border-[#031D47] hover:bg-[#031D47]/5 transition-colors"
              >
                <div>
                  <p className="dta-overline text-navy text-[0.6rem]">{slot}</p>
                  <p className="font-display uppercase font-bold text-lg mt-1">
                    Suggest a {slot}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#031D47]" strokeWidth={1.75} />
              </button>
            );
          }
          return (
            <MealCard
              key={slot}
              meal={meal}
              slotRemaining={{
                calories: dailyTarget.calories * (slot === "breakfast" ? 0.25 : slot === "lunch" ? 0.35 : 0.4),
                protein: dailyTarget.protein * (slot === "breakfast" ? 0.25 : slot === "lunch" ? 0.35 : 0.4),
                carbs: dailyTarget.carbs * (slot === "breakfast" ? 0.25 : slot === "lunch" ? 0.35 : 0.4),
                fat: dailyTarget.fat * (slot === "breakfast" ? 0.25 : slot === "lunch" ? 0.35 : 0.4),
              }}
              onSwap={() => swap(slot)}
              onRemove={() => remove(slot)}
            />
          );
        })}

        {/* Topup list */}
        {topups.length > 0 && (
          <div className="bg-white border border-black/10 p-5">
            <p className="dta-overline text-navy">DTA top-ups</p>
            <ul className="mt-3 divide-y divide-black/10">
              {topups.map((t) => (
                <li key={t._uid} className="flex items-center justify-between py-2 gap-3">
                  <div>
                    <p className="text-sm text-[#272727]">{t.name}</p>
                    <p className="dta-overline text-[0.55rem] text-[#272727]/60 mt-0.5">
                      {t.calories} kcal · {t.protein}g protein
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTopup(t._uid)}
                    className="text-[#272727]/60 hover:text-[#031D47]"
                    aria-label="Remove"
                  >
                    <X className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Sidebar: summary + 5-a-day + top-ups */}
      <div className="lg:col-span-5 space-y-4">
        <SummaryTable target={dailyTarget} planned={planned} />
        <FiveADay
          portions={portions + portionsFromTopups(topups)}
          onInc={() => setPortions((p) => Math.min(5, p + 1))}
          onDec={() => setPortions((p) => Math.max(0, p - 1))}
        />
        <TopupPicker onAdd={addTopup} />
        <IndependenceNoticeShort provider={provider} />
      </div>
    </div>
  );
}

/* ---------- Week builder ---------- */

function WeekBuilder({ provider, catalogue, dailyTarget }) {
  const [days, setDays] = useState(5);
  const plan = useMemo(() => buildWeekPlan(catalogue, dailyTarget, days), [catalogue, dailyTarget, days]);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="dta-overline text-navy">Plan length</p>
          <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-1">
            Build my week.
          </h4>
        </div>
        <div className="inline-flex border border-black/15 bg-white" role="tablist">
          {[3, 5, 7].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              data-testid={`week-days-${n}`}
              className={`px-4 py-2 dta-overline text-[0.65rem] transition-colors ${
                days === n ? "bg-[#031D47] text-white" : "text-[#272727]"
              }`}
            >
              {n}-day
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plan.map((d, idx) => (
          <div key={idx} className="bg-white border border-black/10 p-4" data-testid={`week-day-${idx}`}>
            <p className="dta-overline text-navy">Day {idx + 1}</p>
            <ul className="mt-3 space-y-2">
              {SLOTS.map((slot) => {
                const m = d[slot];
                return (
                  <li key={slot} className="border-t border-black/10 pt-2">
                    <p className="dta-overline text-[0.55rem] text-[#272727]/60">{slot}</p>
                    <p className="text-sm font-medium text-[#0A0A0A] mt-0.5">{m ? m.name : "—"}</p>
                    {m && (
                      <p className="dta-overline text-[0.55rem] text-[#272727]/70 mt-1">
                        {m.calories} kcal · P{m.protein} C{m.carbs} F{m.fat}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 pt-3 border-t border-black/10 flex items-baseline justify-between">
              <p className="dta-overline text-[0.55rem] text-[#272727]/60">Daily total</p>
              <p className="font-display font-bold text-sm text-[#031D47]">
                {d.totals.calories} kcal
              </p>
            </div>
          </div>
        ))}
      </div>

      <IndependenceNoticeShort provider={provider} />
    </div>
  );
}

function buildWeekPlan(catalogue, target, days) {
  const byType = {
    breakfast: catalogue.filter((m) => m.meal_type === "breakfast"),
    lunch: catalogue.filter((m) => m.meal_type === "lunch"),
    dinner: catalogue.filter((m) => m.meal_type === "dinner"),
  };
  const plan = [];
  for (let i = 0; i < days; i++) {
    const d = {};
    for (const slot of SLOTS) {
      const pool = byType[slot];
      d[slot] = pool.length ? pool[(i + slot.length) % pool.length] : null;
    }
    d.totals = sumTotals(SLOTS.map((s) => d[s]));
    plan.push(d);
  }
  return plan;
}

/* ---------- Main component ---------- */

export default function MealPlanner({ dailyTarget }) {
  const [provider, setProvider] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(null); // null | "frive" | "self" | "week"
  const [selectedByType, setSelectedByType] = useState({ breakfast: null, lunch: null, dinner: null, topups: [] });
  const [showAllergenGate, setShowAllergenGate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, c] = await Promise.all([
          axios.get(`${API}/providers/frive`),
          axios.get(`${API}/catalogue/frive`),
        ]);
        if (cancelled) return;
        setProvider(p.data);
        setCatalogue(c.data);
      } catch (e) {
        if (!cancelled) setError("Couldn't load meal catalogue. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-seed day with suggested meals once catalogue loads + mode=frive
  useEffect(() => {
    if (mode !== "frive" || catalogue.length === 0) return;
    const next = { ...selectedByType };
    SLOTS.forEach((slot) => {
      if (!next[slot]) {
        next[slot] = suggestMealForSlot({ slot, dailyTarget, planned: sumTotals([]), catalogue });
      }
    });
    setSelectedByType(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, catalogue]);

  if (loading) {
    return (
      <div className="bg-white border border-black/10 p-6 md:p-8 text-sm text-[#272727]/70">
        Loading meal catalogue…
      </div>
    );
  }
  if (error || !provider) {
    return (
      <div className="bg-white border border-black/10 p-6 md:p-8 text-sm text-red-800">
        {error || "Meal planner unavailable."}
      </div>
    );
  }

  const outboundUrl = provider.outbound_url + (provider.affiliate_code ? `?ref=${provider.affiliate_code}` : "");

  return (
    <div className="space-y-6" data-testid="meal-planner">
      {/* Build my day header */}
      <div className="bg-[#031D47] text-white p-6 md:p-8">
        <div className="flex items-center gap-3">
          <ChefHat className="w-5 h-5 text-white/70" strokeWidth={1.75} />
          <p className="dta-overline text-white/60">Build my day</p>
        </div>
        <h3 className="font-display uppercase font-bold text-2xl md:text-4xl mt-3 leading-tight">
          Turn those numbers into actual food.
        </h3>
        <p className="mt-4 text-sm md:text-base text-white/75 max-w-2xl leading-relaxed">
          DTA can suggest prepared meals and simple everyday foods that may help you get closer to your daily target.
        </p>
      </div>

      {/* Independence notice — long form, first time Frive appears */}
      <IndependenceNoticeLong provider={provider} />

      {/* Choose a meal option */}
      {!mode && (
        <div data-testid="meal-mode-chooser">
          <p className="dta-overline text-navy">Choose a meal option</p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setMode("frive")}
              data-testid="meal-mode-frive"
              className="text-left border border-black/15 bg-white p-6 hover:border-[#031D47] hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3">
                <UtensilsCrossed className="w-5 h-5 text-[#031D47]" strokeWidth={1.75} />
                <p className="dta-overline text-navy">Prepared meals</p>
              </div>
              <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-3 leading-tight">
                Suggested {provider.name} meals
              </h4>
              <p className="text-sm text-[#272727]/80 mt-3 leading-relaxed">
                Browse suggested {provider.name} meals based on how closely they fit your calculated macros.
              </p>
            </button>
            <button
              type="button"
              onClick={() => setMode("self")}
              data-testid="meal-mode-self"
              className="text-left border border-black/15 bg-white p-6 hover:border-[#031D47] hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3">
                <Hammer className="w-5 h-5 text-[#031D47]" strokeWidth={1.75} />
                <p className="dta-overline text-navy">Do it yourself</p>
              </div>
              <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-3 leading-tight">
                Build it myself
              </h4>
              <p className="text-sm text-[#272727]/80 mt-3 leading-relaxed">
                Use your DTA target as a daily goal and plan your own meals using home cooking or any other provider.
              </p>
            </button>
          </div>
        </div>
      )}

      {/* Build it myself path */}
      {mode === "self" && (
        <div className="bg-white border border-black/10 p-6 md:p-8 space-y-5" data-testid="meal-self-mode">
          <p className="dta-overline text-navy">Build it myself</p>
          <h4 className="font-display uppercase font-bold text-2xl md:text-3xl leading-tight">
            Your target. Your way.
          </h4>
          <p className="text-sm md:text-base text-[#272727] leading-relaxed max-w-2xl">
            Keep today's target in mind as you plan your own meals — home-cooked, any other provider or a mix. Use the 5-a-day tracker and quick top-ups to round out the day.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SummaryTable target={dailyTarget} planned={sumTotals(selectedByType.topups || [])} />
            <div className="space-y-4">
              <FiveADayStandalone />
              <TopupPicker
                onAdd={(f) =>
                  setSelectedByType({
                    ...selectedByType,
                    topups: [...(selectedByType.topups || []), { ...f, _uid: Date.now() + Math.random() }],
                  })
                }
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMode(null)}
            className="dta-overline text-[0.65rem] text-[#031D47] hover:opacity-70"
          >
            ← Choose a different option
          </button>
        </div>
      )}

      {/* Frive mode: day builder + tabs for week */}
      {(mode === "frive" || mode === "week") && (
        <div className="space-y-6">
          <div className="flex items-center flex-wrap gap-3">
            <div className="inline-flex border border-black/15 bg-white" role="tablist">
              <button
                type="button"
                onClick={() => setMode("frive")}
                data-testid="meal-tab-day"
                className={`px-5 py-2.5 dta-overline text-[0.65rem] ${
                  mode === "frive" ? "bg-[#031D47] text-white" : "text-[#272727]"
                }`}
              >
                Build my day
              </button>
              <button
                type="button"
                onClick={() => setMode("week")}
                data-testid="meal-tab-week"
                className={`px-5 py-2.5 dta-overline text-[0.65rem] border-l border-black/15 ${
                  mode === "week" ? "bg-[#031D47] text-white" : "text-[#272727]"
                }`}
              >
                Build my week
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMode(null)}
              className="dta-overline text-[0.65rem] text-[#031D47] hover:opacity-70"
            >
              ← Choose a different option
            </button>
          </div>

          {mode === "frive" && (
            <DayBuilder
              provider={provider}
              catalogue={catalogue}
              dailyTarget={dailyTarget}
              selectedByType={selectedByType}
              setSelectedByType={setSelectedByType}
            />
          )}

          {mode === "week" && (
            <WeekBuilder provider={provider} catalogue={catalogue} dailyTarget={dailyTarget} />
          )}

          {/* Outbound order block */}
          <div className="bg-white border-2 border-[#031D47] p-6 md:p-8" data-testid="meal-outbound-block">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="max-w-xl">
                <p className="dta-overline text-navy">Ready to order?</p>
                <h4 className="font-display uppercase font-bold text-xl md:text-2xl mt-2 leading-tight">
                  View meals with {provider.name}.
                </h4>
                <p className="mt-3 text-sm text-[#272727]/80 leading-relaxed">
                  You'll leave DTA and continue directly on the {provider.name} website. DTA does not place the order or collect your payment details.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAllergenGate(true)}
                data-testid="meal-outbound-button"
                className="dta-btn dta-btn-dark whitespace-nowrap"
              >
                View meals with {provider.name}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
            <IndependenceNoticeShort provider={provider} />
          </div>
        </div>
      )}

      {showAllergenGate && (
        <OutboundGate
          provider={provider}
          onCancel={() => setShowAllergenGate(false)}
          onProceed={() => {
            setShowAllergenGate(false);
            window.open(outboundUrl, "_blank", "noopener,noreferrer");
          }}
        />
      )}
    </div>
  );
}

/* ---------- Standalone 5-a-day for self mode ---------- */
function FiveADayStandalone() {
  const [p, setP] = useState(0);
  return (
    <FiveADay
      portions={p}
      onInc={() => setP((x) => Math.min(5, x + 1))}
      onDec={() => setP((x) => Math.max(0, x - 1))}
    />
  );
}
